import { PolicySearchResponse, PolicySearchRequestQuery } from '@lio/api-types';
import { config } from '@services/config';

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { buildQueryParamForArray } from '@utils/buildQueryParamForArray';

export const PolicySearchKeys = {
  search: ['policySearchCustomer'] as const,
  searchParams: (params: PolicySearchRequestQuery) => [...PolicySearchKeys.search, params] as const,
};

const getQueryParams = (params: PolicySearchRequestQuery) => {
  const { pageLimit, pageOffset, customerId, status, sort, insuranceProductId } = params;
  const query = new URLSearchParams();

  if (pageLimit) {
    query.append('pageLimit', String(pageLimit));
  }

  if (pageOffset) {
    query.append('pageOffset', String(pageOffset));
  }

  if (customerId) {
    query.append('customerId', customerId);
  }

  if (status?.length) {
    buildQueryParamForArray(query, 'status', status);
  }

  if (insuranceProductId) {
    query.append('insuranceProductId', insuranceProductId);
  }

  if (sort) {
    query.append('sort', sort);
  }

  return query.toString();
};

// TODO, expand and use all params (then this can be usePolicySearch and phase out usePolicySearch)
export const usePolicySearchForCustomer = (params: PolicySearchRequestQuery, enabled: boolean = true) => {
  const client = useClient();

  const query = getQueryParams({ ...params });

  const result = useQuery<PolicySearchResponse, Error>({
    queryKey: PolicySearchKeys.searchParams({ ...params }),
    queryFn: () => client.get(`policy?${query}`).json<PolicySearchResponse>(),
    // API requires at least one filter to be defined
    enabled: enabled && (!!params.customerId || !!params.status?.length || !!params.insuranceProductId),
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    ...config,
  });

  return { ...result, data: result.data as PolicySearchResponse };
};
